import { inject } from '@/generic/authentication/injections.ts';
import { AUTHENTICATION } from '@/generic/authentication/AuthenticationKeys.ts';
import { RequestedPage } from '@/generic/authentication/domain/RequestedPage.ts';
import { type ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  IpponButton,
  IpponCard,
  IpponContainer,
  IpponErrorArea,
  IpponHSpace,
  IpponText,
} from '@ippon-ui/react';

const SigninCallbackCard = ({ children }: { children: ReactNode }) => (
  <IpponContainer>
    <IpponHSpace align="center">
      <IpponCard size="large" shadow="l3">
        {children}
      </IpponCard>
    </IpponHSpace>
  </IpponContainer>
);

const SigningInComponent = () => {
  const { t } = useTranslation('authentication');

  return (
    <SigninCallbackCard>
      <IpponText variant="body" dataSelector="authentication.sign-in-callback">
        {t('authentication.sign-in-callback')}
      </IpponText>
    </SigninCallbackCard>
  );
};

const SignInErrorComponent = () => {
  const { t } = useTranslation('authentication');

  return (
    <SigninCallbackCard>
      <IpponErrorArea
        title={t('authentication.sign-in-error.title')}
        description={t('authentication.sign-in-error.description')}
        dataSelector="authentication.sign-in-error"
      >
        <IpponButton
          iconLeft={{ name: 'log-in' }}
          onClick={() => inject(AUTHENTICATION).loginAgain()}
          dataSelector="authentication.sign-in-error.login-again"
        >
          {t('authentication.sign-in-error.login-again')}
        </IpponButton>
      </IpponErrorArea>
    </SigninCallbackCard>
  );
};

export const SigninCallback = () => {
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    inject(AUTHENTICATION)
      .loggedIn()
      .then((requestedPage: RequestedPage) => navigate(requestedPage.path))
      .catch(() => setFailed(true));
  }, [navigate]);

  if (failed) {
    return <SignInErrorComponent />;
  }

  return <SigningInComponent />;
};
